import React, { useState, useEffect } from 'react';
import { Box, Tabs, Tab, Typography, Paper } from '@mui/material';
import { useSnackbar } from 'notistack';

import DomainTable from '../components/approvals/DomainTable';
import ClientTable from '../components/approvals/ClientTable';
import EngagementTable from '../components/approvals/EngagementTable';
import WorkflowTable from '../components/approvals/WorkflowTable';
import UserTable from '../components/approvals/UserTable';
import GrantClient from '../components/approvals/GrantClient';

import { getPendingClientApi } from "../api's/ClientApi";
import { getPendingEngagementApi } from "../api's/EngagementApi";
import { getPendingWorkflowApi } from "../api's/WorkflowApi";
import { getPendingUserApi } from "../api's/UserApi";
import { getPendingDomainApi } from "../api's/DomainApi";

import LoadingIcon from '../reusable-component/LoadingIcon';
import ClientNoData from '../components/Client/ClientNoData';

import {
  CLIENT_PAGE,
  ENGAGEMENT_PAGE,
  WORKFLOW_PAGE,
  USER_PAGE,
  APPROVAL_PAGE,
  DOMAIN_PAGE,
} from '../utils/commonConstants';

const defaultPagination = { page: 0, size: 5, total: 0 };

const ApprovalPage = () => {
  const { enqueueSnackbar } = useSnackbar();
  const [value, setValue] = useState(0);

  // ---------- STATES ----------
  const [domainData, setDomainData] = useState([]);
  const [domainPagination, setDomainPagination] = useState(defaultPagination);

  const [clientData, setClientData] = useState([]);
  const [clientPagination, setClientPagination] = useState(defaultPagination);

  const [engagementData, setEngagementData] = useState([]);
  const [engagementPagination, setEngagementPagination] = useState(defaultPagination);

  const [workflowData, setWorkflowData] = useState([]);
  const [workflowPagination, setWorkflowPagination] = useState(defaultPagination);

  const [userData, setUserData] = useState([]);
  const [userPagination, setUserPagination] = useState(defaultPagination);

  const [loading, setLoading] = useState(false);

  // ---------- COMMON ERROR HANDLER ----------
  const handleError = (error) => {
    if (!error?.response) return;

    const status = error.response.status;
    if (status === 403) {
      enqueueSnackbar("You don't have permission!", { variant: 'error' });
    } else {
      enqueueSnackbar('Internal server error!', { variant: 'error' });
    }
  };

  // ---------- FETCH FUNCTIONS ----------

  const fetchDomain = async (page = 0) => {
    setLoading(true);
    try {
      const res = await getPendingDomainApi(page, domainPagination.size);
      setDomainData(res.data.data);
      setDomainPagination({
        page,
        size: domainPagination.size,
        total: res.data.totalData,
      });
    } catch (error) {
      setDomainData([]);
      handleError(error);
    }
    setLoading(false);
  };

  const fetchClient = async (page = 0) => {
    setLoading(true);
    try {
      const res = await getPendingClientApi(page, clientPagination.size);
      setClientData(res.data.data);
      setClientPagination({
        page,
        size: clientPagination.size,
        total: res.data.totalData,
      });
    } catch (error) {
      setClientData([]);
      handleError(error);
    }
    setLoading(false);
  };

  const fetchEngagement = async (page = 0) => {
    setLoading(true);
    try {
      const res = await getPendingEngagementApi(page, engagementPagination.size);
      setEngagementData(res.data.data);
      setEngagementPagination({
        page,
        size: engagementPagination.size,
        total: res.data.totalData,
      });
    } catch (error) {
      setEngagementData([]);
      handleError(error);
    }
    setLoading(false);
  };

  const fetchWorkflow = async (page = 0) => {
    setLoading(true);
    try {
      const res = await getPendingWorkflowApi(page, workflowPagination.size);
      setWorkflowData(res.data.data);
      setWorkflowPagination({
        page,
        size: workflowPagination.size,
        total: res.data.totalData,
      });
    } catch (error) {
      setWorkflowData([]);
      handleError(error);
    }
    setLoading(false);
  };

  const fetchUser = async (page = 0) => {
    setLoading(true);
    try {
      const res = await getPendingUserApi(page, userPagination.size);
      setUserData(res.data.data);
      setUserPagination({
        page,
        size: userPagination.size,
        total: res.data.totalData,
      });
    } catch (error) {
      setUserData([]);
      handleError(error);
    }
    setLoading(false);
  };

  // ---------- ON LOAD ----------
  useEffect(() => {
    fetchDomain();
    fetchClient();
    fetchEngagement();
    fetchWorkflow();
    fetchUser();
  }, []);

  const handleTabChange = (event, newValue) => {
    setValue(newValue);

    // Fetch data for selected tab (reset to first page)
    if (newValue === 0) fetchDomain(0);
    if (newValue === 1) fetchClient(0);
    if (newValue === 2) fetchEngagement(0);
    if (newValue === 3) fetchWorkflow(0);
    if (newValue === 4) fetchUser(0);
  };

  return (
    <Box>
      <Typography sx={{ color: '#43425D' }}>{APPROVAL_PAGE.approvalsPageTitle}</Typography>

      <Tabs value={value} onChange={handleTabChange} TabIndicatorProps={{ style: { backgroundColor: '#47D7AC' } }}>
        <Tab label={`Domain(${domainPagination.total})`} />
        <Tab label={`Client(${clientPagination.total})`} />
        <Tab label={`Engagement(${engagementPagination.total})`} />
        <Tab label={`Workflow(${workflowPagination.total})`} />
        <Tab label={`User(${userPagination.total})`} />
        <Tab label="Grant Client" />
      </Tabs>

      {/* -------- DOMAIN -------- */}
      {value === 0 &&
        (domainData.length > 0 ? (
          <DomainTable tableData={domainData} pagination={domainPagination} onPageChange={fetchDomain} />
        ) : (
          !loading && (
            <Paper elevation={3} sx={{ height: '350px', marginTop: '15px' }}>
              <ClientNoData title={DOMAIN_PAGE.domainPageNoDataToDisplay} text={DOMAIN_PAGE.noDomainToReview} />
            </Paper>
          )
        ))}

      {/* -------- CLIENT -------- */}
      {value === 1 &&
        (clientData.length > 0 ? (
          <ClientTable tableData={clientData} pagination={clientPagination} onPageChange={fetchClient} />
        ) : (
          !loading && (
            <Paper elevation={3} sx={{ height: '350px', marginTop: '15px' }}>
              <ClientNoData title={CLIENT_PAGE.clientPageNoDataToDisplay} text={CLIENT_PAGE.noClientToReview} />
            </Paper>
          )
        ))}

      {/* -------- ENGAGEMENT -------- */}
      {value === 2 &&
        (engagementData.length > 0 ? (
          <EngagementTable
            tableData={engagementData}
            pagination={engagementPagination}
            onPageChange={fetchEngagement}
          />
        ) : (
          !loading && (
            <Paper elevation={3} sx={{ height: '350px', marginTop: '15px' }}>
              <ClientNoData
                title={ENGAGEMENT_PAGE.engagementPageNoDataToDisplay}
                text={ENGAGEMENT_PAGE.noEngagementToReview}
              />
            </Paper>
          )
        ))}

      {/* -------- WORKFLOW -------- */}
      {value === 3 &&
        (workflowData.length > 0 ? (
          <WorkflowTable tableData={workflowData} pagination={workflowPagination} onPageChange={fetchWorkflow} />
        ) : (
          !loading && (
            <Paper elevation={3} sx={{ height: '350px', marginTop: '15px' }}>
              <ClientNoData title={WORKFLOW_PAGE.workflowPageNoDataToDisplay} text={WORKFLOW_PAGE.noWorkflowToReview} />
            </Paper>
          )
        ))}

      {/* -------- USER -------- */}
      {value === 4 &&
        (userData.length > 0 ? (
          <UserTable tableData={userData} pagination={userPagination} onPageChange={fetchUser} />
        ) : (
          !loading && (
            <Paper elevation={3} sx={{ height: '350px', marginTop: '15px' }}>
              <ClientNoData title={USER_PAGE.userPageNoDataToDisplay} text={USER_PAGE.noUserToReview} />
            </Paper>
          )
        ))}

      {/* -------- GRANT CLIENT -------- */}
      {value === 5 && <GrantClient />}

      {loading && <LoadingIcon />}
    </Box>
  );
};

export default ApprovalPage;
